/**
 * Iter 144 — Master "Marketable Days" Calendar.
 *
 * A single-page library of every day that matters for content / marketing
 * planning: public holidays, festivals, awareness days, hallmark commercial
 * events. Users can filter by country/category/industry and add custom days
 * scoped to their workspace + brand.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { CalendarDays, Search, Plus, Sparkles, Upload, Share2 } from "lucide-react";

const CATEGORY_TONE = {
  public_holiday: "text-red-600 border-red-400/40 bg-red-50/40",
  festival:       "text-orange-600 border-orange-400/40 bg-orange-50/40",
  awareness_day:  "text-purple-600 border-purple-400/40 bg-purple-50/40",
  commercial:     "text-pink-600 border-pink-400/40 bg-pink-50/40",
  sporting:       "text-blue-600 border-blue-400/40 bg-blue-50/40",
  environmental:  "text-emerald-600 border-emerald-400/40 bg-emerald-50/40",
  cultural:       "text-amber-600 border-amber-400/40 bg-amber-50/40",
  religious:      "text-indigo-600 border-indigo-400/40 bg-indigo-50/40",
  national_pride: "text-rose-600 border-rose-400/40 bg-rose-50/40",
};

export default function MarketableDays() {
  const { activeWorkspaceId } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [country, setCountry] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [customDays, setCustomDays] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showCustom, setShowCustom] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Iter 253o · Fan-out selected day
  const [fanOutDay, setFanOutDay] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (country) params.set("countries", country);
      if (category) params.set("categories", category);
      const { data } = await api.get(`/reference/special-days?${params.toString()}`);
      setCatalog(data.days || []);
      const { data: cd } = await api.get(`/workspaces/${activeWorkspaceId}/custom-days?year=${year}`);
      setCustomDays(cd || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (activeWorkspaceId) load(); }, [activeWorkspaceId, year, country, category]); // eslint-disable-line

  const filtered = useMemo(() => {
    const merged = [
      ...customDays.map((c) => ({ ...c, is_custom: true })),
      ...catalog,
    ];
    if (!q.trim()) return merged;
    const s = q.toLowerCase();
    return merged.filter((d) => d.name.toLowerCase().includes(s));
  }, [catalog, customDays, q]);

  const byMonth = useMemo(() => {
    const grouped = {};
    for (const d of filtered) {
      const m = d.date ? d.date.slice(0, 7) : "moveable";
      (grouped[m] = grouped[m] || []).push(d);
    }
    return grouped;
  }, [filtered]);

  return (
    <>
      <PageHeader
        eyebrow="Marketing"
        title="Marketable days"
        description="Every holiday, festival and awareness day that agencies plan campaigns around, across countries and industries. Pick a day to seed a content-calendar entry for a brand, or add a custom day scoped to your workspace."
        testid="marketable-days-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setBulkOpen(true)} data-testid="bulk-import-days-btn">
              <Upload className="w-4 h-4 mr-2" /> Bulk import CSV
            </Button>
            <Button className="rounded-none" onClick={() => setShowCustom(true)} data-testid="add-custom-day-btn">
              <Plus className="w-4 h-4 mr-2" /> Add custom day
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        {/* Filters */}
        <div className="border border-border bg-muted/40 p-3 flex flex-wrap items-center gap-2" data-testid="marketable-filters">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 border border-border rounded-none px-2 text-sm bg-background" data-testid="marketable-year">
            {[year - 1, year, year + 1, year + 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="h-9 border border-border rounded-none px-2 text-sm bg-background" data-testid="marketable-country">
            <option value="">All countries</option>
            <option value="GLOBAL">Global</option>
            <option value="IN">🇮🇳 India</option>
            <option value="US">🇺🇸 United States</option>
            <option value="GB">🇬🇧 United Kingdom</option>
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 border border-border rounded-none px-2 text-sm bg-background" data-testid="marketable-category">
            <option value="">All categories</option>
            <option value="public_holiday">Public holiday</option>
            <option value="festival">Festival</option>
            <option value="awareness_day">Awareness day</option>
            <option value="commercial">Commercial event</option>
            <option value="cultural">Cultural</option>
            <option value="religious">Religious</option>
            <option value="environmental">Environmental</option>
            <option value="national_pride">National pride</option>
          </select>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search days…" className="pl-7 h-9 rounded-none" value={q} onChange={(e) => setQ(e.target.value)} data-testid="marketable-search" />
          </div>
          <span className="ml-auto text-[11px] text-muted-foreground" data-testid="marketable-count">{filtered.length} day{filtered.length === 1 ? "" : "s"}</span>
        </div>

        {busy && !catalog.length ? (
          <div className="text-sm text-muted-foreground p-6">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No days match your filters" description="Try widening country / category or clearing search." icon={CalendarDays} testid="marketable-empty" />
        ) : (
          <div className="space-y-6">
            {Object.entries(byMonth).map(([m, list]) => {
              const monthLabel = m === "moveable"
                ? "Moveable dates (check yearly)"
                : new Date(`${m}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
              return (
                <div key={m} data-testid={`marketable-month-${m}`}>
                  <div className="label-eyebrow text-[10px] text-muted-foreground mb-2">{monthLabel} · {list.length}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {list.map((d, idx) => <DayCard key={`${d.name}-${idx}`} day={d} onReload={load} onFanOut={setFanOutDay} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCustom && (
        <AddCustomDayDialog
          wsId={activeWorkspaceId}
          onClose={() => setShowCustom(false)}
          onCreated={() => { setShowCustom(false); load(); }}
        />
      )}
      {bulkOpen && (
        <BulkImportDialog
          wsId={activeWorkspaceId}
          onClose={() => setBulkOpen(false)}
          onDone={(opts) => { load(); if (!opts?.keepOpen) setBulkOpen(false); }}
        />
      )}
      {fanOutDay && (
        <FanOutDialog
          wsId={activeWorkspaceId}
          day={fanOutDay}
          onClose={() => setFanOutDay(null)}
        />
      )}
    </>
  );
}


// ────────── Iter 149 · Bulk CSV import dialog ──────────

const CSV_TEMPLATE = "name,date,category,emoji,description,industries,content_ideas\nDiwali (custom),2026-10-19,cultural,🪔,Festival of Lights,fashion|retail|food,gifting reel|home decor post\nMonsoon Kickoff,2026-06-01,commercial,🌧,,fashion,rain reels|umbrella campaign";

function BulkImportDialog({ wsId, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [rawText, setRawText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseCSV = (txt) => {
    const lines = txt.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const header = splitCSVLine(lines[0]).map((h) => h.toLowerCase().trim());
    const idx = (n) => header.indexOf(n);
    const iName = idx("name"), iDate = idx("date"), iCat = idx("category"),
          iEmoji = idx("emoji"), iDesc = idx("description"),
          iInd = idx("industries"), iCI = idx("content_ideas");
    return lines.slice(1).map((line) => {
      const cells = splitCSVLine(line);
      return {
        name: (cells[iName] || "").trim(),
        date: (cells[iDate] || "").trim(),
        category: (cells[iCat] || "cultural").trim() || "cultural",
        emoji: iEmoji >= 0 ? (cells[iEmoji] || "").trim() : null,
        description: iDesc >= 0 ? (cells[iDesc] || "").trim() : null,
        industries: iInd >= 0 ? (cells[iInd] || "").split("|").map((s) => s.trim()).filter(Boolean) : [],
        content_ideas: iCI >= 0 ? (cells[iCI] || "").split("|").map((s) => s.trim()).filter(Boolean) : [],
      };
    }).filter((r) => r.name && /^\d{4}-\d{2}-\d{2}$/.test(r.date));
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const txt = await f.text();
    setRawText(txt);
    setRows(parseCSV(txt));
  };

  const onTextChange = (v) => {
    setRawText(v);
    setRows(parseCSV(v));
  };

  const doImport = async () => {
    if (!rows.length) return toast.error("No valid rows detected");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/custom-days/bulk-import`, {
        days: rows, dedupe: true,
      });
      setResult(data);
      toast.success(`${data.inserted} imported · ${data.skipped} skipped${data.errors?.length ? ` · ${data.errors.length} err` : ""}`);
      // Iter 149 followup — kick the parent refresh but keep the dialog open
      // so the user sees the inline result panel with per-status counts.
      // The user closes explicitly via 'Close'.
      if (data.inserted > 0) onDone({ keepOpen: true });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk import failed"));
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "marketable-days-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="bulk-days-dialog">
        <DialogHeader><DialogTitle>Bulk import days of significance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-[10px] uppercase tracking-widest">CSV file</Label>
            <Input type="file" accept=".csv,text/csv" onChange={onFile} className="max-w-xs" data-testid="bulk-days-file" />
            <Button variant="outline" size="sm" className="rounded-none" onClick={downloadTemplate} data-testid="bulk-days-template">
              Download template
            </Button>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Or paste CSV</Label>
            <textarea
              value={rawText}
              onChange={(e) => onTextChange(e.target.value)}
              rows={6}
              className="w-full font-mono text-[11px] border border-border p-2 rounded-none"
              placeholder="name,date,category,…"
              data-testid="bulk-days-textarea"
            />
          </div>
          {rows.length > 0 && (
            <div className="text-[11px] text-muted-foreground" data-testid="bulk-days-preview-count">
              Detected <b>{rows.length}</b> valid row{rows.length === 1 ? "" : "s"}. First: <b>{rows[0].name}</b> on {rows[0].date}.
            </div>
          )}
          {result && (
            <div className="border border-border p-2 text-[11px]" data-testid="bulk-days-result">
              <div>Inserted: <b className="text-emerald-600">{result.inserted}</b></div>
              <div>Skipped (dup): <b className="text-muted-foreground">{result.skipped}</b></div>
              {result.errors?.length > 0 && (
                <div className="text-destructive">Errors: {result.errors.length}</div>
              )}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground italic">
            Columns: name, date (YYYY-MM-DD), category, emoji, description, industries (pipe-separated), content_ideas (pipe-separated). Rows with the same name + date are skipped.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
          <Button className="rounded-none" onClick={doImport} disabled={busy || rows.length === 0} data-testid="bulk-days-import-btn">
            {busy ? "Importing…" : `Import ${rows.length} day${rows.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Simple CSV line splitter that handles quoted values.
function splitCSVLine(line) {
  const out = [];
  let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { q = !q; continue; }
    if (c === "," && !q) { out.push(cur); cur = ""; continue; }
    cur += c;
  }
  out.push(cur);
  return out;
}

function DayCard({ day, onReload, onFanOut }) {
  const tone = CATEGORY_TONE[day.category] || "";
  // Iter 253o · Fan-out only makes sense for days with a concrete date.
  const canFanOut = !!day.date;
  return (
    <div className={`border p-3 flex items-start gap-3 ${tone}`} data-testid={`marketable-day-${day.name}`}>
      <div className="w-9 h-9 flex items-center justify-center text-lg shrink-0" style={{ background: day.color || "var(--primary)" }} aria-hidden="true">
        <span className="text-white text-base">{day.emoji || "•"}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-sm">{day.name}</div>
          {day.is_custom && <span className="chip text-[9px] chip-internal">custom</span>}
        </div>
        <div className="text-xs text-muted-foreground font-mono">
          {day.date || "Moveable"}
          {day.date_rule?.notes ? ` · ${day.date_rule.notes}` : ""}
          {day.countries?.length ? ` · ${day.countries.join(", ")}` : ""}
        </div>
        {(day.content_ideas || []).length > 0 && (
          <ul className="mt-1 text-[11px] list-disc pl-4 space-y-0.5">
            {day.content_ideas.slice(0, 3).map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        )}
        {canFanOut && (
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-border hover:bg-background/60"
            onClick={() => onFanOut?.(day)}
            data-testid={`marketable-fan-out-${day.name}`}
            title="Fan out to selected brands — creates a bucket item, a calendar entry, and one task per brand"
          >
            <Share2 className="w-3 h-3" /> Fan out to brands
          </button>
        )}
      </div>
      {day.is_custom && (
        <button
          type="button"
          className="text-[10px] text-destructive underline shrink-0"
          onClick={async () => {
            if (!window.confirm(`Delete custom day "${day.name}"?`)) return;
            try {
              await api.delete(`/workspaces/${day.workspace_id}/custom-days/${day.id}`);
              toast.success("Deleted");
              onReload();
            } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
          }}
          data-testid={`marketable-day-delete-${day.id}`}
        >Delete</button>
      )}
    </div>
  );
}

// ────────── Iter 253o · Fan-out dialog ──────────

function FanOutDialog({ wsId, day, onClose }) {
  const [brands, setBrands] = useState([]);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({
    description: "",
    format: "instagram_caption",
    channel: "instagram",
    content_type: "post",
    pillar: "",
    task_assignee_id: "",
    task_due_offset_days: 3,
    task_priority: "medium",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get(`/workspaces/${wsId}/brands`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
    ]).then(([b, m]) => {
      if (cancel) return;
      const barr = Array.isArray(b.data) ? b.data : (b.data?.items || []);
      setBrands(barr);
      setMembers(Array.isArray(m.data) ? m.data : []);
    });
    return () => { cancel = true; };
  }, [wsId]);

  const toggle = (bid) => {
    setSelected((s) => s.includes(bid) ? s.filter((x) => x !== bid) : [...s, bid]);
  };
  const selectAll = () => setSelected(brands.map((b) => b.id));
  const clear = () => setSelected([]);

  const submit = async () => {
    if (!selected.length) { toast.error("Pick at least one brand"); return; }
    setBusy(true);
    try {
      const payload = {
        day_name: day.name,
        date: day.date,
        brand_ids: selected,
        description: form.description || null,
        format: form.format,
        channel: form.channel,
        content_type: form.content_type,
        pillar: form.pillar || null,
        task_assignee_id: form.task_assignee_id || null,
        task_due_offset_days: Number(form.task_due_offset_days) || 0,
        task_priority: form.task_priority,
      };
      const { data } = await api.post(
        `/workspaces/${wsId}/marketable-days/fan-out`, payload);
      setResult(data);
      toast.success(`Fanned out to ${data.succeeded}/${data.total} brand(s).`);
    } catch (e) { toast.error(apiErrorMessage(e, "Fan-out failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="fanout-dialog">
        <DialogHeader>
          <DialogTitle>Fan out · {day.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            For each selected brand we'll create <strong>1 content bucket draft</strong> +
            <strong> 1 content calendar entry</strong> on {day.date} +
            <strong> 1 task</strong> due {form.task_due_offset_days} day{form.task_due_offset_days === 1 ? "" : "s"} before.
          </div>

          <div className="border border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="label-eyebrow">Brands ({selected.length}/{brands.length})</Label>
              <div className="flex gap-1">
                <button type="button" className="text-[10px] underline"
                        onClick={selectAll} data-testid="fanout-select-all">All</button>
                <button type="button" className="text-[10px] underline text-muted-foreground"
                        onClick={clear} data-testid="fanout-clear">Clear</button>
              </div>
            </div>
            {brands.length === 0 && (
              <div className="text-xs text-muted-foreground italic">No brands yet. Add a brand first.</div>
            )}
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
              {brands.map((b) => {
                const on = selected.includes(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => toggle(b.id)}
                    className={`text-[11px] px-2 py-1 border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                    data-testid={`fanout-brand-${b.id}`}
                  >
                    {b.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Bucket format</Label>
              <select value={form.format} onChange={(e) => setForm((s) => ({ ...s, format: e.target.value }))}
                      className="h-9 w-full border border-border rounded-none px-2 text-sm bg-background"
                      data-testid="fanout-format">
                <option value="instagram_caption">Instagram post</option>
                <option value="instagram_carousel">Instagram carousel</option>
                <option value="instagram_reel_script">Reel script</option>
                <option value="linkedin_post">LinkedIn post</option>
                <option value="x_post">X post</option>
                <option value="blog_post">Blog post</option>
                <option value="email_newsletter">Email newsletter</option>
                <option value="whatsapp_broadcast">WhatsApp broadcast</option>
                <option value="poster_image">Poster</option>
                <option value="short_video_script">Short video script</option>
                <option value="youtube_script">YouTube script</option>
              </select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Channel</Label>
              <select value={form.channel} onChange={(e) => setForm((s) => ({ ...s, channel: e.target.value }))}
                      className="h-9 w-full border border-border rounded-none px-2 text-sm bg-background"
                      data-testid="fanout-channel">
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter/X</option>
                <option value="facebook">Facebook</option>
                <option value="tiktok">TikTok</option>
                <option value="youtube">YouTube</option>
              </select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Pillar (optional)</Label>
              <Input value={form.pillar} onChange={(e) => setForm((s) => ({ ...s, pillar: e.target.value }))}
                     className="h-9 rounded-none" placeholder="e.g. Awareness"
                     data-testid="fanout-pillar" />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow mb-1 inline-block">Description / brief (shared across brands)</Label>
            <Input value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                   className="rounded-none" placeholder="Optional — appears on every generated bucket item + task"
                   data-testid="fanout-description" />
          </div>

          <div className="border border-border p-3 bg-muted/20">
            <div className="label-eyebrow mb-2">Task (one per brand)</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Assignee</Label>
                <select value={form.task_assignee_id}
                        onChange={(e) => setForm((s) => ({ ...s, task_assignee_id: e.target.value }))}
                        className="h-9 w-full border border-border rounded-none px-2 text-sm bg-background"
                        data-testid="fanout-assignee">
                  <option value="">Unassigned</option>
                  {members.map((m) => (
                    <option key={m.user_id || m.id} value={m.user_id || m.id}>
                      {m.name || m.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Due N days before</Label>
                <Input type="number" min={0} max={60} value={form.task_due_offset_days}
                       onChange={(e) => setForm((s) => ({ ...s, task_due_offset_days: e.target.value }))}
                       className="h-9 rounded-none"
                       data-testid="fanout-due-offset" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Priority</Label>
                <select value={form.task_priority}
                        onChange={(e) => setForm((s) => ({ ...s, task_priority: e.target.value }))}
                        className="h-9 w-full border border-border rounded-none px-2 text-sm bg-background"
                        data-testid="fanout-priority">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>

          {result && (
            <div className="border border-border p-3 bg-muted/20 text-xs" data-testid="fanout-result">
              <div className="font-semibold mb-1">Fan-out result</div>
              <div className="text-muted-foreground mb-2">
                {result.succeeded} succeeded · {result.failed} failed · {(result.missing_brands || []).length} missing
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(result.results || []).map((r) => (
                  <div key={r.brand_id} className="flex items-center justify-between border-b border-border/50 pb-1">
                    <span>{r.brand_name || r.brand_id}</span>
                    {r.error
                      ? <span className="text-rose-600 text-[10px]">{r.error}</span>
                      : <span className="text-emerald-600 text-[10px]">✓ bucket + calendar + task</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="fanout-close">
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button className="rounded-none" onClick={submit} disabled={busy || selected.length === 0} data-testid="fanout-submit">
              {busy ? "Fanning out…" : `Create for ${selected.length} brand${selected.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCustomDayDialog({ wsId, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", date: "", category: "cultural",
    emoji: "", color: "#f97316", description: "",
    recurrence_kind: "none", recurrence_interval: 1, recurrence_until: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name || !form.date) { toast.error("Name and date required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name, date: form.date, category: form.category,
        emoji: form.emoji, color: form.color, description: form.description,
      };
      if (form.recurrence_kind && form.recurrence_kind !== "none") {
        payload.recurrence = {
          kind: form.recurrence_kind,
          interval: Number(form.recurrence_interval) || 1,
          until_date: form.recurrence_until || null,
        };
      }
      await api.post(`/workspaces/${wsId}/custom-days`, payload);
      toast.success("Custom day added");
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to add")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="add-custom-day-dialog">
        <DialogHeader><DialogTitle>Add custom day</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Name</Label>
            <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="custom-day-name" placeholder="Company anniversary" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} data-testid="custom-day-date" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Category</Label>
              <select value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} className="h-10 w-full border border-border rounded-none px-2 text-sm bg-background" data-testid="custom-day-category">
                <option value="cultural">Cultural</option>
                <option value="commercial">Commercial</option>
                <option value="awareness_day">Awareness day</option>
                <option value="festival">Festival</option>
                <option value="religious">Religious</option>
                <option value="national_pride">National pride</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Emoji</Label>
              <Input maxLength={2} value={form.emoji} onChange={(e) => setForm((s) => ({ ...s, emoji: e.target.value }))} data-testid="custom-day-emoji" placeholder="🎉" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Color</Label>
              <Input type="color" value={form.color || "#f97316"} onChange={(e) => setForm((s) => ({ ...s, color: e.target.value }))} className="h-10 p-1" data-testid="custom-day-color" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Description</Label>
            <Input value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} data-testid="custom-day-description" placeholder="Optional notes for the calendar" />
          </div>
          <div className="border border-border p-2">
            <Label className="label-eyebrow mb-1 inline-block">Recurrence</Label>
            <div className="grid grid-cols-3 gap-2">
              <select value={form.recurrence_kind} onChange={(e) => setForm((s) => ({ ...s, recurrence_kind: e.target.value }))} className="h-9 border border-border rounded-none px-2 text-sm bg-background" data-testid="custom-day-recurrence-kind">
                <option value="none">One-off</option>
                <option value="yearly">Yearly</option>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="custom">Every N days</option>
              </select>
              {form.recurrence_kind !== "none" && (
                <>
                  <Input type="number" min={1} value={form.recurrence_interval}
                         onChange={(e) => setForm((s) => ({ ...s, recurrence_interval: e.target.value }))}
                         className="h-9 text-sm" placeholder="Interval (1)" data-testid="custom-day-recurrence-interval" />
                  <Input type="date" value={form.recurrence_until}
                         onChange={(e) => setForm((s) => ({ ...s, recurrence_until: e.target.value }))}
                         className="h-9 text-sm" placeholder="Until (optional)" data-testid="custom-day-recurrence-until" />
                </>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 italic">
              Recurring days expand automatically on brand cockpits + campaign calendars.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="custom-day-save">
            {busy ? "Saving…" : "Add day"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
